/*
Shared YouTube video component for use in PublicationFormManager
Lightweight version focused only on YouTube URL functionality
Autor: Assistant
Fecha: June 23, 2025
*/
import React, { useState, useEffect } from 'react'
import { Button } from '@radix-ui/themes'

import Vehicle from '../interfaces/Vehicle'
import useVehicleVideoPoster from '../hooks/useVehicleVideoPoster'
import { isValidYouTubeUrl, extractYouTubeId } from '../utils/youtubeUtils'

import InputField from './InputField'

interface VehicleVideoTabProps {
  vehicle: Vehicle
  canModify: boolean
  onRefresh: () => void
}

const VehicleVideoTab: React.FC<VehicleVideoTabProps> = ({ vehicle, canModify, onRefresh }) => {
  // YouTube URL state
  const [youtubeUrl, setYoutubeUrl] = useState<string>(vehicle.videoUrl || '')
  const [youtubeInputValue, setYoutubeInputValue] = useState<string>(vehicle.videoUrl || '')
  const [youtubeError, setYoutubeError] = useState<string>('')
  const [youtubeShowPreview, setYoutubeShowPreview] = useState<boolean>(!!vehicle.videoUrl)
  const [youtubeSuccessMsg, setYoutubeSuccessMsg] = useState<string>('')

  // Hooks
  const { postVideoUrl, loadingPost, errorPostVideo } = useVehicleVideoPoster()

  // Update state when vehicle prop changes (handles cases where vehicle data loads after component mount)
  useEffect(() => {
    const videoUrl = vehicle.videoUrl || ''
    setYoutubeUrl(videoUrl)
    setYoutubeInputValue(videoUrl)
    setYoutubeShowPreview(!!videoUrl && isValidYouTubeUrl(videoUrl))

    // Clear any previous error/success messages when vehicle changes
    setYoutubeError('')
    setYoutubeSuccessMsg('')
  }, [vehicle.videoUrl, vehicle._id])

  // YouTube URL handlers
  const handleYoutubeInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setYoutubeInputValue(e.target.value)
    setYoutubeError('')
    setYoutubeSuccessMsg('')

    if (isValidYouTubeUrl(e.target.value)) {
      setYoutubeUrl(e.target.value)
      setYoutubeShowPreview(true)
    } else {
      setYoutubeShowPreview(false)
    }
  }

  const handleYoutubeSave = async () => {
    if (!isValidYouTubeUrl(youtubeInputValue)) {
      setYoutubeError('Por favor ingresa una URL válida de YouTube.')

      return
    }

    setYoutubeError('')
    setYoutubeSuccessMsg('')

    const result = await postVideoUrl({
      vehicleId: vehicle._id,
      videoUrl: youtubeInputValue,
    })

    if (result === 'OK') {
      setYoutubeUrl(youtubeInputValue)
      setYoutubeShowPreview(true)
      setYoutubeSuccessMsg('Video de YouTube guardado exitosamente.')
      onRefresh()
    } else {
      setYoutubeError('Error al guardar el video de YouTube.')
    }
  }

  const youtubeVideoId = extractYouTubeId(youtubeUrl)

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600 mb-4">
        Agrega una URL de YouTube para mostrar el video del vehículo en las publicaciones.
      </div>

      {/* Input Section */}
      <div className="space-y-4">
        <InputField
          label="URL del video de YouTube"
          name="youtubeUrl"
          type="text"
          value={youtubeInputValue}
          handleInputChange={handleYoutubeInputChange}
          errors={youtubeError ? { youtubeUrl: youtubeError } : {}}
          disabled={!canModify}
        />

        <div className="flex gap-2">
          <Button onClick={handleYoutubeSave} disabled={!canModify || !youtubeInputValue || loadingPost} size="2">
            {loadingPost ? 'Guardando...' : 'Guardar Video'}
          </Button>
        </div>

        {/* Status Messages */}
        {youtubeSuccessMsg && <div className="text-green-600 text-sm">{youtubeSuccessMsg}</div>}
        {youtubeError && <div className="text-red-600 text-sm">{youtubeError}</div>}
        {errorPostVideo && <div className="text-red-600 text-sm">{errorPostVideo.message}</div>}
      </div>

      {/* Preview Section */}
      {youtubeShowPreview && youtubeVideoId && (
        <div className="mt-6">
          <div className="font-semibold mb-3 text-gray-700">Vista previa del video</div>
          <div className="flex justify-center">
            <iframe
              width="560"
              height="315"
              src={`https://www.youtube.com/embed/${youtubeVideoId}`}
              title="YouTube video preview"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="rounded-lg shadow-md max-w-full"
            ></iframe>
          </div>
        </div>
      )}

      {/* No Video State */}
      {!vehicle.videoUrl && !youtubeShowPreview && (
        <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
          <p>No hay video asignado a este vehículo.</p>
          <p className="text-sm">Agrega una URL de YouTube arriba para mostrar el video.</p>
        </div>
      )}
    </div>
  )
}

export default VehicleVideoTab
